/**
 * ============================================================================
 * [P3-03] useScripts Hook
 *
 * 스크립트(scripts) 데이터 관리를 위한 커스텀 훅
 *
 * 기능:
 * - 실시간 구독으로 스크립트 목록 동기화
 * - CRUD 함수 (saveScript, editScript, removeScript)
 *
 * Safety:
 * - useEffect cleanup으로 메모리 누수 방지
 * - 낙관적 업데이트 + 에러 시 롤백
 * - user?.uid 의존성으로 불필요한 재구독 방지
 *
 * [2026-01-28] P3-03 Firestore 연동 구현
 * ============================================================================
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from './useAuth'
import {
  createScript,
  updateScript,
  deleteScript,
  subscribeToScripts,
} from '@/services/firestore'
import type { Script, ScriptFilterState } from '@/types'

// ============================================================================
// [P3-03-H] 반환 타입 정의
// ============================================================================
export interface UseScriptsReturn {
  // State
  scripts: Script[]
  isLoading: boolean
  error: Error | null

  // CRUD
  saveScript: (script: Omit<Script, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Script | null>
  editScript: (id: string, updates: Partial<Script>) => Promise<boolean>
  removeScript: (id: string) => Promise<boolean>

  // 기타
  refresh: () => void

  // [P2-05] 필터링 추가
  filter: ScriptFilterState
  setFilter: (filter: Partial<ScriptFilterState>) => void
  resetFilter: () => void
  filteredScripts: Script[]
  categories: string[]
}

// ============================================================================
// [P3-03-A] useScripts Hook 구현
// ============================================================================
// ============================================================================
// [P2-01] 기본 필터 상태 (Hook 외부에 정의하여 안정적인 참조 유지)
// ============================================================================
const DEFAULT_FILTER: ScriptFilterState = {
  search: '',
  category: '',
  sortBy: 'newest',
}

// ============================================================================
// [P3-03-A] useScripts Hook 구현
// ============================================================================
export function useScripts(): UseScriptsReturn {
  // ========================================================================
  // Auth 상태 가져오기
  // ========================================================================
  const { user } = useAuth()

  // ========================================================================
  // Local State
  // ========================================================================
  const [scripts, setScripts] = useState<Script[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  // [P2-01] 필터 상태 추가
  const [filter, setFilterState] = useState<ScriptFilterState>(DEFAULT_FILTER)

  // ========================================================================
  // [P2-02] 카테고리 목록 추출 (자동 메모이제이션)
  // ========================================================================
  const categories = useMemo(() => {
    const set = new Set<string>()
    scripts.forEach((s) => {
      if (s.category?.trim()) {
        set.add(s.category.trim())
      }
    })
    return Array.from(set).sort()
  }, [scripts])

  // ========================================================================
  // [P2-03] 필터링된 스크립트 목록
  // ========================================================================
  const filteredScripts = useMemo(() => {
    let result = [...scripts]

    // 1. 검색 필터
    if (filter.search.trim()) {
      const searchLower = filter.search.toLowerCase()
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(searchLower) ||
          s.content.toLowerCase().includes(searchLower) ||
          s.category?.toLowerCase().includes(searchLower)
      )
    }

    // 2. 카테고리 필터
    if (filter.category) {
      result = result.filter((s) => s.category === filter.category)
    }

    // 3. 정렬
    result.sort((a, b) => {
      switch (filter.sortBy) {
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        case 'name':
          return a.name.localeCompare(b.name)
        case 'category':
          return (a.category ?? '').localeCompare(b.category ?? '')
        case 'newest':
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      }
    })

    return result
  }, [scripts, filter])

  // ========================================================================
  // [P2-04] 필터 설정 함수
  // ========================================================================
  const setFilter = useCallback((newFilter: Partial<ScriptFilterState>) => {
    setFilterState((prev) => ({ ...prev, ...newFilter }))
  }, [])

  const resetFilter = useCallback(() => {
    setFilterState(DEFAULT_FILTER)
  }, [])

  // ========================================================================
  // [P3-03-B] 실시간 구독 useEffect
  // - CRITICAL: cleanup에서 unsubscribe 호출 필수
  // - 의존성: user?.uid만 사용 (user 객체 전체를 사용하면 불필요한 재구독 발생)
  // ========================================================================
  useEffect(() => {
    // 1. 로그아웃 상태 처리
    if (!user) {
      setScripts([])
      setIsLoading(false)
      setError(null)
      return
    }

    // 2. 로딩 시작
    setIsLoading(true)

    // 3. 구독 시작
    const unsubscribe = subscribeToScripts(
      user.uid,
      (newScripts) => {
        setScripts(newScripts)
        setIsLoading(false)
        setError(null)
      },
      (err) => {
        setError(err)
        setIsLoading(false)
      }
    )

    // 4. CRITICAL: Cleanup - 메모리 누수 방지
    return () => {
      unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid])
  // Note: user?.uid만 의존성에 포함하여 uid 변경 시에만 재구독

  // ========================================================================
  // [P3-03-C] saveScript 함수 (Create)
  // ========================================================================
  const saveScript = useCallback(
    async (
      scriptData: Omit<Script, 'id' | 'createdAt' | 'updatedAt'>
    ): Promise<Script | null> => {
      // 1. Auth Check
      if (!user) {
        console.warn('[useScripts.saveScript] User not authenticated')
        return null
      }

      // 2. Validation
      if (!scriptData.name?.trim()) {
        setError(new Error('스크립트 이름을 입력하세요'))
        return null
      }

      try {
        // 3. Create in Firestore
        const newScript = await createScript(user.uid, scriptData)

        // 4. 실시간 구독이 Store 업데이트 처리함
        setError(null)
        return newScript
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        console.error('[useScripts.saveScript] Error:', error)
        return null
      }
    },
    [user]
  )

  // ========================================================================
  // [P3-03-D] editScript 함수 (Update) - 낙관적 업데이트
  // ========================================================================
  const editScript = useCallback(
    async (id: string, updates: Partial<Script>): Promise<boolean> => {
      // 1. Auth Check
      if (!user) {
        console.warn('[useScripts.editScript] User not authenticated')
        return false
      }

      // 2. Validation
      if (!id) {
        setError(new Error('Script ID is required'))
        return false
      }

      // 3. 기존 데이터 백업 (롤백용)
      const originalScript = scripts.find((s) => s.id === id)
      if (!originalScript) {
        setError(new Error('Script not found'))
        return false
      }

      try {
        // 4. 낙관적 업데이트 (즉시 UI 반영)
        setScripts((prev) =>
          prev.map((s) => (s.id === id ? { ...s, ...updates } : s))
        )

        // 5. Firestore 업데이트
        await updateScript(user.uid, id, updates)

        setError(null)
        return true
      } catch (err) {
        // 6. 롤백 - 원본 데이터로 복원
        setScripts((prev) =>
          prev.map((s) => (s.id === id ? originalScript : s))
        )

        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        console.error('[useScripts.editScript] Error:', error)
        return false
      }
    },
    [user, scripts]
  )

  // ========================================================================
  // [P3-03-E] removeScript 함수 (Delete) - 낙관적 업데이트
  // ========================================================================
  const removeScript = useCallback(
    async (id: string): Promise<boolean> => {
      // 1. Auth Check
      if (!user) {
        console.warn('[useScripts.removeScript] User not authenticated')
        return false
      }

      // 2. Validation
      if (!id) {
        setError(new Error('Script ID is required'))
        return false
      }

      // 3. 기존 데이터 백업 (롤백용)
      const originalScript = scripts.find((s) => s.id === id)

      try {
        // 4. 낙관적 삭제 (즉시 UI 반영)
        setScripts((prev) => prev.filter((s) => s.id !== id))

        // 5. Firestore 삭제
        await deleteScript(user.uid, id)

        setError(null)
        return true
      } catch (err) {
        // 6. 롤백 - 삭제된 항목 복원
        if (originalScript) {
          setScripts((prev) => [...prev, originalScript])
        }

        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        console.error('[useScripts.removeScript] Error:', error)
        return false
      }
    },
    [user, scripts]
  )

  // ========================================================================
  // [P3-03-F] refresh 함수
  // ========================================================================
  const refresh = useCallback(() => {
    // 실시간 구독이 자동으로 새로고침 처리
    // 필요 시 수동 재로드 로직 추가 가능
    console.log('[useScripts] refresh called')
  }, [])

  // ========================================================================
  // Return
  // ========================================================================
  return {
    // State
    scripts,
    isLoading,
    error,

    // CRUD
    saveScript,
    editScript,
    removeScript,

    // 기타
    refresh,

    // [P2-05] 필터링
    filter,
    setFilter,
    resetFilter,
    filteredScripts,
    categories,
  }
}

// ============================================================================
// 기본 export
// ============================================================================
export default useScripts
